/**
 * The site list filter/sort pipeline, shared by every surface that lists sites.
 *
 * Extracted from Templates/Sites/page.vue so Itineraries/add-sites.vue can use
 * the same one (Asana O2-54). Add Sites had its own cut-down copy that applied
 * the search term and nothing else, so its filter panel opened, closed, and
 * changed nothing.
 *
 * Pure on purpose — no refs, no stores, no route access. Callers unwrap their
 * own reactive sources and pass plain values in, which is what makes this
 * testable and what stops the two surfaces drifting apart again.
 *
 * The behaviour here is load-bearing and each rule was a ticket:
 *  - `datesFilters === 'all'` means open BOTH days, not "no filter".
 *  - Closed detection goes through isClosedHours, not a `!== 'closed'` string
 *    compare, which missed "n/a" and "Closed " with a trailing space.
 *  - Near Me keeps `distance <= 1` tested as a NUMBER, so standing on a site
 *    (distance 0) does not drop it.
 *  - Alpha sort honours Airtable's Sortkey and ignores a leading "The".
 */
// Relative, not `@/`: this module is exercised directly by vitest, which does
// not resolve the Nuxt alias. Same reason as server/utils/sync/images.ts.
import { isClosedHours } from '../composables/useSiteLabel';
import { compareSitesByName } from '../composables/useSiteSort';

export const coerceArray = (val: unknown): any[] =>
    Array.isArray(val) ? val : val === undefined || val === null || val === '' ? [] : [val];

/** `nearMe` arrives from the URL, so it is `true`, `'true'` or `false`. */
export const isNearMeActive = (nearMe: unknown): boolean => nearMe === true || nearMe === 'true';

export interface SiteFilterInput {
    sites: any[];
    searchTerm?: string;
    /** (site, map) => neighborhood name, for search matching. */
    getNeighborhoodName?: (site: any, map: any) => string | undefined;
    neighborhoodsMap?: any;
    siteTypeFilters?: unknown;
    amenitiesFilters?: unknown;
    datesFilters?: string;
    nearMe?: unknown;
    sortBy?: string;
    coords?: any;
    /** Injected so this stays free of the location store. */
    calculateDistance?: (lat: number, lng: number) => number;
    userSites?: string[];
}

export function filterAndSortSites(input: SiteFilterInput): any[] {
    const {
        sites, searchTerm = '', getNeighborhoodName, neighborhoodsMap,
        siteTypeFilters, amenitiesFilters, datesFilters, nearMe,
        sortBy = 'alpha', coords, calculateDistance, userSites = [],
    } = input;

    let filtered = Array.isArray(sites) ? sites : [];
    const nearMeOn = isNearMeActive(nearMe);

    if (searchTerm && searchTerm.length) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter((el) => {
            const hood = getNeighborhoodName ? getNeighborhoodName(el, neighborhoodsMap) : undefined;
            return el?.fields?.Site?.toLowerCase().includes(term) || hood?.toLowerCase().includes(term);
        });
    }

    const types = coerceArray(siteTypeFilters);
    if (types.length) {
        filtered = filtered.filter((el) => types.includes(el?.fields?.['Site Type']));
    }

    const amenities = coerceArray(amenitiesFilters);
    if (amenities.length) {
        filtered = filtered.filter((item) =>
            amenities.some((a) => a in (item?.fields ?? {}) && item.fields[a] === 'Yes'));
    }

    if (datesFilters && datesFilters.length) {
        const openOn = (item: any, field: string) =>
            field in (item?.fields ?? {}) && !isClosedHours(item.fields[field]);
        filtered = filtered.filter((item) =>
            datesFilters === 'all'
                ? openOn(item, 'Saturday Hours') && openOn(item, 'Sunday Hours')
                : openOn(item, datesFilters));
    }

    if ((nearMeOn || sortBy === 'distance') && coords && calculateDistance) {
        filtered = filtered.map((site) => {
            const geo = site?.fields?.Geocoordinates;
            if (geo) {
                const [lat, lng] = String(geo).split(',').map((c: string) => parseFloat(c.trim()));
                if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
                    return { ...site, distance: calculateDistance(lat, lng) };
                }
            }
            return { ...site, distance: Infinity };
        });
    }

    if (nearMeOn) {
        filtered = filtered.filter((s) => typeof s.distance === 'number' && s.distance <= 1);
    }

    if (sortBy === 'alpha') filtered = [...filtered].sort(compareSitesByName);
    if (sortBy === 'distance') {
        filtered = [...filtered].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    }
    if (sortBy === 'favorites' && userSites?.length) {
        const matched = filtered.filter((o) => userSites.includes(o.id));
        const unmatched = filtered.filter((o) => !userSites.includes(o.id));
        filtered = [...matched, ...unmatched];
    }

    return filtered;
}
